import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";

export const AutocompleteInput = forwardRef(function AutocompleteInput({
  value,
  onChange,
  onSelect,
  suggestions,
  placeholder,
  className,
  maxSuggestions = 5,
}, forwardedRef) {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Expose the underlying <input> element to parent refs so callers can
  // focus()/select()/scrollIntoView() on it (e.g. after creating a new
  // practice block, we want the title text pre-selected for typing-over).
  useImperativeHandle(forwardedRef, () => inputRef.current, []);

  // Filter suggestions based on input value
  useEffect(() => {
    if (!value.trim()) {
      setFilteredSuggestions([]);
      return;
    }

    const filtered = suggestions
      .filter((suggestion) =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      )
      .slice(0, maxSuggestions);

    setFilteredSuggestions(filtered);
  }, [value, suggestions, maxSuggestions]);

  // Handle clicks outside the component
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    } else if (onChange) {
      onChange({ target: { value: suggestion } });
    }
    setShowSuggestions(false);
  };

  // Handle input changes
  const handleChange = (e) => {
    onChange(e);
    setShowSuggestions(true);
  };

  // Handle input focus
  const handleFocus = () => {
    if (value && filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <ul
          ref={suggestionsRef}
          className="absolute z-10 w-full bg-white shadow-lg rounded-lg border border-border mt-1 max-h-48 overflow-y-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-2 hover:bg-surface-sunken cursor-pointer transition-colors"
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default AutocompleteInput;
